import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Tag, Share2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError('Article non trouvé');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'd MMMM yyyy', { locale: fr });
    } catch {
      return dateString;
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Lien copié !');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading font-bold text-2xl text-stone-900 mb-4">{error}</h1>
            <Link to="/blog">
              <Button className="btn-primary">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au blog
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="bg-stone-50 py-8 pt-32" data-testid="blog-detail-header">
        <div className="container-custom">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-stone-600 hover:text-emerald-600 transition-colors mb-6"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
        </div>
      </section>

      {/* Hero Image */}
      <section className="relative h-64 md:h-96" data-testid="blog-detail-hero">
        <img 
          src={post.image_url} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0">
          <div className="container-custom pb-8">
            <span className="inline-block bg-emerald-600 text-white text-sm font-medium px-4 py-1 rounded-full mb-4">
              {post.category}
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">
              {post.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12" data-testid="blog-detail-content">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-stone-200">
              <div className="flex items-center gap-2 text-stone-500 text-sm">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </div>
              <div className="flex items-center gap-2 text-stone-500 text-sm">
                <Tag className="w-4 h-4" />
                {post.category}
              </div>
              <button 
                onClick={handleShare}
                className="ml-auto flex items-center gap-2 text-emerald-600 hover:text-emerald-700 transition-colors text-sm font-medium"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4" />
                Partager
              </button>
            </div>

            {/* Excerpt */}
            <p className="text-xl text-stone-600 leading-relaxed mb-8">
              {post.excerpt}
            </p>

            {/* Content */}
            <div 
              className="prose prose-stone prose-lg max-w-none prose-headings:font-heading prose-headings:font-semibold prose-a:text-emerald-600 prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* CTA */}
            <div className="mt-12 p-8 bg-emerald-50 rounded-2xl text-center">
              <h3 className="font-heading font-bold text-xl text-stone-900 mb-4">
                Besoin d'aide pour votre jardin ?
              </h3>
              <p className="text-stone-600 mb-6">
                Nos experts sont à votre disposition pour tous vos projets d'entretien.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/entretien">
                  <Button className="btn-primary">Réserver un entretien</Button>
                </Link>
                <Link to="/services">
                  <Button variant="outline" className="btn-secondary">Tous nos services</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogDetailPage;
